import { DependencyList, useEffect, useState } from "react"

import { catchErrors } from "@src/shared/api"

export type ResourceHook<T> = {
  data: Partial<T>
  isPending: boolean
  load: () => void
}

export function useResourceLoader<T>(
  loader: () => Promise<T>,
  deps?: DependencyList,
): ResourceHook<T> {
  const [state, setState] = useState<{
    data: Partial<T>
    isFailed: boolean
    isPending: boolean
  }>({
    data: {},
    isFailed: false,
    isPending: true,
  })

  const load = () => {
    setState((state) => ({ ...state, isFailed: false, isPending: true }))

    loader()
      .then((response) => {
        setState((state) => ({ ...state, data: response, isPending: false }))

        return response
      })
      .catch((error) => {
        setState((state) => ({ ...state, isFailed: true, isPending: false }))

        catchErrors(error)
      })
  }

  useEffect(() => {
    load()
  }, deps || [])

  return {
    ...state,
    load: load,
  }
}

type Relation<T, F> = {
  condition: boolean
  fetch: Promise<F>
  field: keyof T
}

export function relationsOf<T>(
  target: T,
  relations: Relation<T, any>[],
): Promise<T> {
  return Promise.all(
    relations.map(({ condition, fetch, field }) => {
      return condition
        ? fetch.then((object) => (target[field] = object))
        : Promise.resolve()
    }),
  ).then(() => target)
}

export function hasOne<T, F = any>(
  field: keyof T,
  condition: boolean,
  fetch: Promise<F>,
): Relation<T, F> {
  return { condition, fetch, field }
}
